import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Play, Sun, Sunset, Moon, Coffee, Award, ArrowRight, RefreshCw, Clock } from "lucide-react";

const MODELS = [
  { key: "openai/gpt-5.2", label: "GPT-5.2" },
  { key: "anthropic/claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
  { key: "gemini/gemini-3-flash-preview", label: "Gemini 3 Flash" },
];

const TIME_ICONS = ["Sun", "Coffee", "Sunset", "Moon", "Sun", "Coffee", "Moon"];

export default function Simulator() {
  const [careers, setCareers] = useState([]);
  const [tab, setTab] = useState("day");
  const [career, setCareer] = useState("");
  const [model, setModel] = useState(MODELS[0].key);
  const [busy, setBusy] = useState(false);

  const [dayResult, setDayResult] = useState(null);
  const [session, setSession] = useState(null); // interactive

  useEffect(() => {
    api.get("/careers/").then((r) => { setCareers(r.data); if (r.data[0] && !career) setCareer(r.data[0].id); });
  }, []); // eslint-disable-line

  const runDay = async () => {
    if (!career) return;
    setBusy(true); setDayResult(null);
    try {
      const r = await api.post("/simulator/day-in-life", { career_id: career, model });
      setDayResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const startScenario = async () => {
    if (!career) return;
    setBusy(true); setSession(null);
    try {
      const r = await api.post("/simulator/scenario/start", { career_id: career, model });
      setSession(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const choose = async (index) => {
    if (!session) return;
    setBusy(true);
    try {
      const r = await api.post("/simulator/scenario/choose", { session_id: session.session_id, choice_index: index });
      setSession({ ...session, state: r.data.state });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-6 py-10" data-testid="simulator-page">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Career Simulator</p>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Try before you commit.</h1>
        <p className="text-slate-400 mt-2 max-w-xl">Live a day in the life or step through real scenarios of any career — powered by AI.</p>

        {/* Controls */}
        <div className="mt-8 brimi-card p-6 grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Career</label>
            <select data-testid="sim-career-select" value={career} onChange={(e) => { setCareer(e.target.value); setDayResult(null); setSession(null); }}
              className="mt-2 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 outline-none focus:ring-2 focus:ring-blue-100">
              {careers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">AI Model</label>
            <select data-testid="sim-model-select" value={model} onChange={(e) => setModel(e.target.value)}
              className="mt-2 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 outline-none focus:ring-2 focus:ring-blue-100">
              {MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <div className="flex gap-2 w-full">
              <button data-testid="sim-tab-day" onClick={() => setTab("day")} className={`flex-1 px-4 py-2.5 rounded-full text-sm font-bold border transition ${tab === "day" ? "text-slate-900" : "border-slate-200 text-slate-700"}`} style={tab === "day" ? { background: "#FBBF24", borderColor: "#FBBF24" } : {}}>Day in Life</button>
              <button data-testid="sim-tab-scenario" onClick={() => setTab("scenario")} className={`flex-1 px-4 py-2.5 rounded-full text-sm font-bold border transition ${tab === "scenario" ? "text-slate-900" : "border-slate-200 text-slate-700"}`} style={tab === "scenario" ? { background: "#FBBF24", borderColor: "#FBBF24" } : {}}>Interactive</button>
            </div>
          </div>
        </div>

        {/* Day in Life */}
        {tab === "day" && (
          <div className="mt-6" data-testid="sim-day-panel">
            <button data-testid="sim-day-run" onClick={runDay} disabled={busy || !career} className="brimi-pill accent-coral flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Generate a day in the life</>}
            </button>

            {dayResult && (
              <div className="mt-6 space-y-6" data-testid="sim-day-result">
                <div className="brimi-card p-6">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">A day in the life of a</p>
                  <h2 className="font-display text-3xl text-slate-900 mt-1">{dayResult.career_name}</h2>
                  {dayResult.result?.location_setting && <p className="text-slate-600 mt-2">{dayResult.result.location_setting}</p>}
                </div>

                <div className="brimi-card p-6">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Your day</p>
                  <div className="mt-4 space-y-4">
                    {(dayResult.result?.moments || []).map((m, i) => (
                      <div key={i} className="flex gap-4 pb-4 border-b border-slate-100 last:border-0">
                        <div className="w-16 flex-shrink-0 text-right">
                          <p className="font-display text-sm font-bold text-slate-900">{m.time}</p>
                        </div>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: momentColor(i) + "22", color: momentColor(i) }}>
                          <Clock className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-slate-900">{m.title}</p>
                          <p className="text-sm text-slate-600 mt-0.5">{m.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {dayResult.result?.vibe_check && (
                  <div className="brimi-card p-6 border-l-4" style={{ borderLeftColor: "#FB7185" }}>
                    <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "#FB7185" }}>Honest vibe check</p>
                    <p className="text-slate-800 mt-2">{dayResult.result.vibe_check}</p>
                  </div>
                )}

                {dayResult.result?.start_now_skills?.length > 0 && (
                  <div className="brimi-card p-6">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "#34D399" }}>Skills to start building now</p>
                    <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                      {dayResult.result.start_now_skills.map((s, i) => (
                        <li key={i} className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-slate-800 text-sm font-semibold">✓ {s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Interactive */}
        {tab === "scenario" && (
          <div className="mt-6" data-testid="sim-scenario-panel">
            {!session && (
              <button data-testid="sim-scenario-start" onClick={startScenario} disabled={busy || !career} className="brimi-pill accent-coral flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4" /> Start scenario</>}
              </button>
            )}

            {session && (
              <div className="mt-6 space-y-5" data-testid="sim-scenario-active">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300 font-bold">Turn {session.state?.turn || 1}{session.state?.done ? " · Final" : " of 5"}</p>
                  <button data-testid="sim-scenario-restart" onClick={startScenario} className="text-xs font-bold text-slate-400 hover:text-white flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Restart</button>
                </div>

                {session.state?.outcome && (
                  <div className="brimi-card p-5 border-l-4" style={{ borderLeftColor: "#3B82F6" }}>
                    <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "#3B82F6" }}>Outcome</p>
                    <p className="text-slate-800 mt-2">{session.state.outcome}</p>
                  </div>
                )}

                {session.state?.scenario && !session.state?.done && (
                  <div className="brimi-card p-6">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Scenario</p>
                    <p className="font-display text-xl text-slate-900 mt-2">{session.state.scenario}</p>
                    <div className="mt-5 space-y-2">
                      {(session.state.choices || []).map((c) => (
                        <button key={c.index} data-testid={`sim-choice-${c.index}`}
                          onClick={() => choose(c.index)} disabled={busy}
                          className="w-full text-left p-4 rounded-xl border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition text-slate-800 font-semibold flex items-center justify-between gap-3 disabled:opacity-60">
                          <span>{c.label}</span>
                          <ArrowRight className="w-4 h-4 flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Skills / traits running list */}
                {((session.state?.skills_gained?.length || 0) > 0 || (session.state?.traits_shown?.length || 0) > 0) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="brimi-card p-5">
                      <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "#34D399" }}>Skills gained</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(session.state.skills_gained || []).map((s, i) => <span key={i} className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 font-bold">{s}</span>)}
                      </div>
                    </div>
                    <div className="brimi-card p-5">
                      <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "#FB7185" }}>Traits shown</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(session.state.traits_shown || []).map((t, i) => <span key={i} className="text-xs px-2 py-1 rounded-full bg-pink-50 text-pink-800 font-bold">{t}</span>)}
                      </div>
                    </div>
                  </div>
                )}

                {session.state?.done && (
                  <div className="brimi-card p-8" data-testid="sim-scenario-final">
                    <div className="flex items-center gap-3">
                      <Award className="w-8 h-8" style={{ color: "#FBBF24" }} />
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">Session complete</p>
                        <h3 className="font-display text-2xl text-slate-900">Your career fit for {session.career_name}</h3>
                      </div>
                    </div>
                    {session.state.match_score !== undefined && (
                      <div className="mt-4 flex items-center gap-3">
                        <span className="font-display text-4xl text-slate-900">{session.state.match_score}<span className="text-lg text-slate-500">/100</span></span>
                        <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full" style={{ width: `${session.state.match_score}%`, background: "linear-gradient(90deg, #FB7185, #FBBF24, #34D399)" }} />
                        </div>
                      </div>
                    )}
                    {session.state.final_reflection && (
                      <div className="mt-5 p-4 rounded-xl bg-yellow-50 border border-yellow-200">
                        <p className="text-slate-800">{session.state.final_reflection}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function momentColor(i) {
  const colors = ["#FBBF24", "#FB7185", "#3B82F6", "#34D399", "#8B5CF6", "#F97316", "#EC4899"];
  return colors[i % colors.length];
}
